import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPreferences, savePreferences } from '../api';

const DEFAULTS = {
  defaultQty: 5,
  stockMaxPositionPct: 5, etfMaxPositionPct: 15,
  stockAllocationPct: 20, cashReservePct: 20,
  stopLossEnabled: true, stopLossPct: 8,
  takeProfitEnabled: true, takeProfitPct: 20,
  dailyLimitEnabled: false, dailyLimitPct: null, dailyLimitAmt: null,
  weeklyLimitEnabled: false, weeklyLimitPct: null, weeklyLimitAmt: null,
  minComposite: 0.30, enableStocks: true, enableEtfs: true, excludeSymbols: '', focusSymbols: '',
};

export default function PreferencesPage() {
  const [saved, setSaved] = useState(DEFAULTS);
  const [draft, setDraft] = useState(DEFAULTS);
  const [toast, setToast] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Load from API first, fall back to localStorage
    getPreferences()
      .then(prefs => {
        if (prefs) {
          const merged = { ...DEFAULTS, ...prefs };
          setSaved(merged); setDraft(merged);
          localStorage.setItem('tm_preferences', JSON.stringify(merged));
        } else {
          const stored = localStorage.getItem('tm_preferences');
          if (stored) try { const p = { ...DEFAULTS, ...JSON.parse(stored) }; setSaved(p); setDraft(p); } catch {}
        }
      })
      .catch(() => {
        const stored = localStorage.getItem('tm_preferences');
        if (stored) try { const p = { ...DEFAULTS, ...JSON.parse(stored) }; setSaved(p); setDraft(p); } catch {}
      });
  }, []);

  const set = (key, val) => {
    setDraft(p => {
      const next = { ...p, [key]: val };
      // Clamp max position to never exceed its allocation from slider
      const cash = next.cashReservePct || 0;
      const invested = 100 - cash;
      const stockAlloc = Math.round(next.stockAllocationPct * invested / 100);
      const etfAlloc = invested - stockAlloc;
      if (next.stockMaxPositionPct > stockAlloc) next.stockMaxPositionPct = stockAlloc;
      if (next.etfMaxPositionPct > etfAlloc) next.etfMaxPositionPct = etfAlloc;
      return next;
    });
  };
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);

  const handleSave = () => {
    localStorage.setItem('tm_preferences', JSON.stringify(draft));
    savePreferences(draft).catch(() => {});
    setSaved({ ...draft });
    setToast('Preferences saved');
    setTimeout(() => setToast(''), 3000);
  };

  const stockPct = draft.stockAllocationPct;
  const etfPct = 100 - stockPct;
  const cashPct = draft.cashReservePct || 0;
  const investedPct = 100 - cashPct;
  const maxStockAlloc = Math.round(stockPct * investedPct / 100);
  const maxEtfAlloc = investedPct - maxStockAlloc;

  return (
    <div>
      <div style={s.header}>
        <div>
          <h1 style={s.h1}>Investment Preferences</h1>
          <p style={s.sub}>Configure how signals are filtered and orders are sized</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {toast && <span style={s.toast}>✓ {toast}</span>}
          <button onClick={() => { setDraft({ ...DEFAULTS }); }} style={s.resetBtn}>Reset</button>
          <button onClick={handleSave} disabled={!dirty} style={{ ...s.saveBtn, opacity: dirty ? 1 : 0.4 }}>Save</button>
        </div>
      </div>

      {/* ── Position Sizing ── */}
      <Section title="Position Sizing">
        <Row label="Default order qty" sub="Shares per order">
          <Num value={draft.defaultQty} onChange={v => set('defaultQty', v)} min={1} max={100} />
        </Row>
        <Row label="Max stock position" sub={`Max % of portfolio in a single stock (up to ${maxStockAlloc}%)`}>
          <Num value={draft.stockMaxPositionPct} onChange={v => set('stockMaxPositionPct', v)} min={1} max={maxStockAlloc} suffix="%" />
        </Row>
        <Row label="Max ETF position" sub={`Max % of portfolio in a single ETF (up to ${maxEtfAlloc}%)`}>
          <Num value={draft.etfMaxPositionPct} onChange={v => set('etfMaxPositionPct', v)} min={1} max={maxEtfAlloc} suffix="%" />
        </Row>
        <div style={s.sliderRow}>
          <div>
            <div style={s.rowLabel}>Stock / ETF split</div>
            <div style={s.rowSub}>Target allocation between stocks and ETFs</div>
          </div>
          <div style={s.sliderWrap}>
            <div style={s.sliderLabels}>
              <span style={{ color: 'var(--green)', fontWeight: 600 }}>Stocks {stockPct}%</span>
              <span style={{ color: '#60a5fa', fontWeight: 600 }}>ETFs {etfPct}%</span>
            </div>
            <div style={s.sliderTrack}>
              <div style={{ ...s.sliderFill, width: `${stockPct}%`, background: 'var(--green)' }} />
              <div style={{ ...s.sliderFill, width: `${etfPct}%`, background: '#60a5fa' }} />
            </div>
            <input type="range" min={0} max={100} step={5} value={stockPct}
              onChange={e => set('stockAllocationPct', Number(e.target.value))} style={s.sliderInput} />
          </div>
        </div>
        <Row label="Cash reserve" sub="Min % of portfolio kept in cash — remainder split per slider">
          <Num value={draft.cashReservePct} onChange={v => set('cashReservePct', v)} min={0} max={50} suffix="%" />
        </Row>
      </Section>

      {/* ── Risk Management ── */}
      <Section title="Risk Management">
        <ToggleRow label="Stop loss" sub="Auto-sell when position drops" checked={draft.stopLossEnabled} onChange={v => set('stopLossEnabled', v)}>
          {draft.stopLossEnabled && <Num value={draft.stopLossPct} onChange={v => set('stopLossPct', v)} min={1} max={50} suffix="%" />}
        </ToggleRow>
        <ToggleRow label="Take profit" sub="Auto-sell when position gains" checked={draft.takeProfitEnabled} onChange={v => set('takeProfitEnabled', v)}>
          {draft.takeProfitEnabled && <Num value={draft.takeProfitPct} onChange={v => set('takeProfitPct', v)} min={5} max={100} suffix="%" />}
        </ToggleRow>
      </Section>

      {/* ── Spending Limits ── */}
      <Section title="Spending Limits">
        <ToggleRow label="Daily limit" sub="Cap total investment per day" checked={draft.dailyLimitEnabled} onChange={v => { set('dailyLimitEnabled', v); if (!v) { set('dailyLimitPct', null); set('dailyLimitAmt', null); } }} />
        {draft.dailyLimitEnabled && (
          <>
            <Row label="Daily limit — % of cash" sub="Max % of available cash per day">
              <Num value={draft.dailyLimitPct} onChange={v => set('dailyLimitPct', v)} min={1} max={100} suffix="%" placeholder="" />
            </Row>
            <Row label="Daily limit — $ amount" sub="Max dollar amount per day">
              <Num value={draft.dailyLimitAmt} onChange={v => set('dailyLimitAmt', v)} min={100} max={1000000} prefix="$" wide placeholder="" />
            </Row>
          </>
        )}
        <ToggleRow label="Weekly limit" sub="Cap total investment per week" checked={draft.weeklyLimitEnabled} onChange={v => { set('weeklyLimitEnabled', v); if (!v) { set('weeklyLimitPct', null); set('weeklyLimitAmt', null); } }} />
        {draft.weeklyLimitEnabled && (
          <>
            <Row label="Weekly limit — % of cash" sub="Max % of available cash per week">
              <Num value={draft.weeklyLimitPct} onChange={v => set('weeklyLimitPct', v)} min={1} max={100} suffix="%" placeholder="" />
            </Row>
            <Row label="Weekly limit — $ amount" sub="Max dollar amount per week">
              <Num value={draft.weeklyLimitAmt} onChange={v => set('weeklyLimitAmt', v)} min={100} max={1000000} prefix="$" wide placeholder="" />
            </Row>
          </>
        )}
      </Section>

      {/* ── Signal Filtering ── */}
      <Section title="Signal Filtering" >
        <Row label="Min composite score" sub="Only show signals above this">
          <Num value={draft.minComposite} onChange={v => set('minComposite', v)} min={0} max={1} step={0.05} />
        </Row>
      </Section>

      {/* ── Asset Universe ── */}
      <Section title="Asset Universe">
        <ToggleRow label="Include stocks" sub="Large-cap equities" checked={draft.enableStocks} onChange={v => set('enableStocks', v)} />
        <ToggleRow label="Include ETFs" sub="$50B+ AUM funds" checked={draft.enableEtfs} onChange={v => set('enableEtfs', v)} />
        <Row label="Focus symbols" sub="Only show these (blank = all)">
          <input value={draft.focusSymbols} onChange={e => set('focusSymbols', e.target.value.toUpperCase())}
            placeholder="AAPL, NVDA, SPY" style={s.textInput} />
        </Row>
        <Row label="Exclude symbols" sub="Never show these">
          <input value={draft.excludeSymbols} onChange={e => set('excludeSymbols', e.target.value.toUpperCase())}
            placeholder="TSLA, MEME" style={s.textInput} />
        </Row>
      </Section>

      <button onClick={() => { handleSave(); navigate('/allocation'); }} style={s.allocLink}>
        <span>📊 Save and View Allocation Preview</span>
        <span style={{ fontSize: 11, color: 'var(--t3)' }}>Current vs target portfolio allocation →</span>
      </button>
    </div>
  );
}

function Section({ title, icon, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div style={s.section}>
      <div style={s.sectionHead} onClick={() => setOpen(!open)}>
        <span style={s.sectionIcon}>{icon}</span>
        <span style={s.sectionTitle}>{title}</span>
        <span style={s.chevron}>{open ? '▾' : '▸'}</span>
      </div>
      {open && <div style={s.sectionBody}>{children}</div>}
    </div>
  );
}

function Row({ label, sub, children }) {
  return (
    <div style={s.row}>
      <div style={s.rowLeft}>
        <div style={s.rowLabel}>{label}</div>
        {sub && <div style={s.rowSub}>{sub}</div>}
      </div>
      <div>{children}</div>
    </div>
  );
}

function ToggleRow({ label, sub, checked, onChange, children }) {
  return (
    <div style={s.row}>
      <div style={s.rowLeft}>
        <div style={s.rowLabel}>{label}</div>
        {sub && <div style={s.rowSub}>{sub}</div>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div onClick={() => onChange(!checked)} style={{ ...s.toggle, background: checked ? 'var(--green)' : 'var(--bg-3)' }}>
          <div style={{ ...s.toggleDot, transform: checked ? 'translateX(16px)' : 'translateX(0)' }} />
        </div>
        {children}
      </div>
    </div>
  );
}

function Num({ value, onChange, min, max, step, suffix, prefix, wide }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
      {prefix && <span style={{ fontSize: 12, color: 'var(--t3)' }}>{prefix}</span>}
      <input type="number" value={value ?? ''} min={min} max={max} step={step || 1}
        placeholder="—"
        onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))}
        style={{ ...s.numInput, width: wide ? 76 : 56 }} />
      {suffix && <span style={{ fontSize: 11, color: 'var(--t3)' }}>{suffix}</span>}
    </div>
  );
}

const s = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  h1: { fontSize: 22, fontWeight: 700, marginBottom: 4 },
  sub: { fontSize: 13, color: 'var(--t3)' },
  toast: { fontSize: 12, color: 'var(--green)', fontWeight: 600 },
  saveBtn: {
    padding: '7px 18px', background: 'var(--green)', color: '#000', border: 'none',
    borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer',
  },
  resetBtn: {
    padding: '7px 14px', background: 'var(--bg-2)', color: 'var(--t3)',
    border: '1px solid var(--border)', borderRadius: 6, fontSize: 12, cursor: 'pointer',
  },

  section: {
    background: 'var(--bg-1)', borderRadius: 'var(--rl)', border: '1px solid var(--border)',
    marginBottom: 12, overflow: 'hidden',
  },
  sectionHead: {
    display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px',
    cursor: 'pointer', userSelect: 'none',
  },
  sectionIcon: { fontSize: 14 },
  sectionTitle: { fontSize: 14, fontWeight: 600, flex: 1 },
  chevron: { fontSize: 11, color: 'var(--t3)' },
  sectionBody: { borderTop: '1px solid var(--border-s)' },

  row: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 18px', borderBottom: '1px solid var(--border-s)',
  },
  rowLeft: { flex: 1, minWidth: 0 },
  rowLabel: { fontSize: 13, fontWeight: 500, color: 'var(--t1)' },
  rowSub: { fontSize: 11, color: 'var(--t3)', marginTop: 1 },

  sliderRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 18px', borderBottom: '1px solid var(--border-s)', gap: 24,
  },
  sliderWrap: { width: 220 },
  sliderLabels: { display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 4 },
  sliderTrack: { display: 'flex', height: 5, borderRadius: 3, overflow: 'hidden', marginBottom: 2 },
  sliderFill: { height: '100%', transition: 'width 0.15s' },
  sliderInput: { width: '100%', margin: 0, cursor: 'pointer', accentColor: 'var(--green)' },

  toggle: {
    width: 36, height: 20, borderRadius: 10, cursor: 'pointer',
    transition: 'background 0.15s', position: 'relative', flexShrink: 0,
  },
  toggleDot: {
    width: 16, height: 16, borderRadius: '50%', background: '#fff',
    position: 'absolute', top: 2, left: 2, transition: 'transform 0.15s',
    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
  },

  numInput: {
    width: 56, padding: '5px 6px', background: 'var(--bg-2)', border: '1px solid var(--border)',
    borderRadius: 5, color: 'var(--t1)', fontSize: 13, textAlign: 'center',
  },
  textInput: {
    width: 180, padding: '5px 10px', background: 'var(--bg-2)', border: '1px solid var(--border)',
    borderRadius: 5, color: 'var(--t1)', fontSize: 12,
  },
  select: {
    padding: '5px 10px', background: 'var(--bg-2)', border: '1px solid var(--border)',
    borderRadius: 5, color: 'var(--t1)', fontSize: 12,
  },

  allocLink: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    width: '100%', boxSizing: 'border-box',
    marginTop: 12, padding: '12px 18px', background: 'var(--bg-1)',
    borderRadius: 'var(--rl)', border: '1px solid var(--border)',
    textDecoration: 'none', color: 'var(--t1)', fontSize: 13, fontWeight: 600,
    cursor: 'pointer',
  },
};
